import React from "react";
import {
  QrCode,
  CreditCard,
  Users,
  BarChart3,
  Clock,
  Shield,
} from "lucide-react";

const FeatureCard = ({
  icon: Icon,
  title,
  description,
  image,
  benefits,
  reverse = false,
}) => {
  return (
    <div
      className={`grid lg:grid-cols-2 gap-12 items-center py-20 ${
        reverse ? "lg:grid-flow-col-dense" : ""
      }`}
    >
      <div className={`space-y-6 ${reverse ? "lg:col-start-2" : ""}`}>
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-gradient-to-br from-orange-500 to-red-600 rounded-xl">
            <Icon className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
            {title}
          </h2>
        </div>
        <p className="text-lg text-gray-600 leading-relaxed max-w-xl">
          {description}
        </p>

        {/* Benefits List */}
        <div className="space-y-3">
          {benefits.map((benefit, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
              <span className="text-gray-700">{benefit}</span>
            </div>
          ))}
        </div>

        {/* <button className="text-orange-600 hover:text-red-600 font-semibold text-lg transition-colors duration-300 group">
          Learn more
          <span className="ml-2 group-hover:translate-x-1 transition-transform inline-block">→</span>
        </button> */}
      </div>

      <div className={`relative ${reverse ? "lg:col-start-1" : ""}`}>
        <img
          src={image}
          alt={title}
          width={"400px"}
          height={"400px"}
          className="w-full h-96 rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
      </div>
    </div>
  );
};

const FeaturesSection = () => {
  const features = [
    {
      icon: QrCode,
      title: "QR-Based Digital Menu",
      description:
        "Customers scan QR code to see full menu with photos, prices & descriptions. Update menu instantly anytime. Hygienic & touchless experience.",
      benefits: [
        "Instant menu updates - prices, items, descriptions",
        "High-quality photos for every dish",
        "Hygienic touchless ordering",
        "Multi-language support",
      ],
      image:
        "./QRFeatures.webp",
      reverse: false,
    },
    {
      icon: CreditCard,
      title: "Integrated Payment Solutions",
      description:
        "All payments in one place - PhonePe, Google Pay, Paytm, cards. No waiting for bills. Secure transactions with instant confirmation.",
      benefits: [
        "All payment methods integrated",
        "Instant payment confirmation",
        "No cash handling required",
        "Fraud protection included",
      ],
      image:
        "./PaymentFeatures.webp",
      reverse: true,
    },
    {
      icon: Users,
      title: "Customer Data Management (CRM)",
      description:
        "Save customer data automatically. Send promotions, birthday offers & rewards. Build loyalty with personalized experiences.",
      benefits: [
        "Automatic customer data collection",
        "Birthday & anniversary reminders",
        "Loyalty program management",
        "Personalized offers & promotions",
      ],
      image:
        "./MarketingFeatures.png",
      reverse: false,
    },
    {
      icon: BarChart3,
      title: "Sales & Performance Analytics",
      description:
        "Complete dashboard with sales reports, popular dishes, peak hours analysis. Make data-driven decisions to boost profits.",
      benefits: [
        "Real-time sales dashboard",
        "Best-selling items analysis",
        "Peak hours identification",
        "Monthly profit reports",
      ],
      image:
        "./SalesFeatures.png",
      reverse: true,
    },
    {
      icon: Clock,
      title: "Reduced Waiter Dependency",
      description:
        "Customers order & pay themselves. Faster service, lower staff costs, higher efficiency. Focus staff on food quality & customer experience.",
      benefits: [
        "Self-service ordering system",
        "Reduced labor costs",
        "Faster table turnover",
        "Staff focus on service quality",
      ],
      image:
        "./WaiterFeatures.webp",
      reverse: false,
    },
  ];

  return (
    <section className="bg-white" id="features">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center py-20">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            FineDine Features That{" "}
            <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
              Transform Your Cafe
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to modernize your cafe and boost profits. From
            QR menus to advanced analytics.
          </p>
        </div>

        {features.map((feature, index) => (
          <FeatureCard
            key={index}
            icon={feature.icon}
            title={feature.title}
            description={feature.description}
            benefits={feature.benefits}
            image={feature.image}
            reverse={feature.reverse}
          />
        ))}
      </div>
    </section>
  );
};

export default FeaturesSection;
